import { Injectable, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Subject } from 'rxjs';
import { environmentURL } from 'src/environments/environments';
import {
  LobbyAtencionActualizarEvento,
  PanelJefaturaActualizarEvento,
  TurnoAsignadoEvento,
} from '../models/citas.models';
import { CitasService } from './citas.service';
import { NotificacionesTurnosService } from './notificaciones-turnos.service';

@Injectable({ providedIn: 'root' })
export class EventosSocketService implements OnDestroy {
  private socket: Socket | null = null;
  private socketLobby: Socket | null = null;
  private representanteId: number | null = null;
  private conectando = false;
  private conectandoLobby = false;

  /** Emite cada evento turno_asignado recibido */
  readonly turnoAsignado$ = new Subject<TurnoAsignadoEvento>();
  /** Emite cuando debe refrescarse el panel de jefatura */
  readonly panelJefaturaActualizar$ = new Subject<PanelJefaturaActualizarEvento>();
  /** Emite cuando la pantalla lobby debe refetch del snapshot */
  readonly lobbyAtencionActualizar$ = new Subject<LobbyAtencionActualizarEvento>();
  /** Emite aviso de operación de socio (eliminar / migrar / restaurar) */
  readonly socioOperacion$ = new Subject<Record<string, unknown>>();

  constructor(
    private citas: CitasService,
    private notificaciones: NotificacionesTurnosService,
  ) {}

  /**
   * Aviso local (misma pestaña) para refrescar panel sin esperar Redis.
   * Complementa publicar_panel_jefatura del backend.
   */
  notificarPanelJefaturaLocal(motivo = 'local'): void {
    const fecha = new Date();
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    this.panelJefaturaActualizar$.next({
      tipo: 'panel_jefatura_actualizar',
      fecha: `${y}-${m}-${d}`,
      motivo,
    });
  }

  /** Conecta socket para representante y/o jefatura de citas */
  conectar(): void {
    if (this.socket?.connected || this.conectando) {
      return;
    }
    const usuarioId = localStorage.getItem('usuario_id');
    const token = localStorage.getItem('token') || environmentURL.webAppClientToken;
    if (!usuarioId) {
      return;
    }
    const usuarioInternoId = parseInt(usuarioId, 10);
    if (Number.isNaN(usuarioInternoId)) {
      return;
    }
    this.conectando = true;
    this.citas.obtenerMiPerfilRepresentante(usuarioInternoId).subscribe({
      next: (perfil) => {
        this.representanteId = perfil.representante_id;
        this.abrirSocket(token, usuarioInternoId);
        this.conectando = false;
      },
      error: () => {
        // Comentario: usuarios de jefatura sin perfil representante también reciben eventos
        this.representanteId = null;
        this.abrirSocket(token, usuarioInternoId);
        this.conectando = false;
      },
    });
  }

  /**
   * Conecta socket en modo lobby (pantalla pública TV).
   * Usa token de cliente y room `lobby`; no requiere sesión de usuario.
   */
  conectarLobby(): void {
    if (this.socketLobby?.connected || this.conectandoLobby) {
      return;
    }
    this.conectandoLobby = true;
    const token = environmentURL.webAppClientToken;
    const baseUrl = environmentURL.apiEventosUrl || 'http://localhost:7074';
    this.desconectarLobby();
    this.socketLobby = io(baseUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: {
        token,
        modo: 'lobby',
      },
    });
    this.socketLobby.on('connect', () => {
      this.conectandoLobby = false;
      console.info('sdm-eventos lobby conectado');
    });
    this.socketLobby.on('lobby_atencion_actualizar', (payload: LobbyAtencionActualizarEvento) => {
      this.lobbyAtencionActualizar$.next(payload);
    });
    this.socketLobby.on('connect_error', (err) => {
      this.conectandoLobby = false;
      console.warn('sdm-eventos lobby error:', err.message);
    });
  }

  /** Abre conexión Socket.IO */
  private abrirSocket(token: string, usuarioInternoId: number): void {
    if (this.socket?.connected) {
      return;
    }
    this.desconectarInterno();
    const baseUrl = environmentURL.apiEventosUrl || 'http://localhost:7074';
    this.socket = io(baseUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: {
        token,
        usuario_interno_id: usuarioInternoId,
      },
    });
    this.socket.on('connect', () => {
      console.info('sdm-eventos conectado');
    });
    this.socket.on('turno_asignado', (payload: TurnoAsignadoEvento) => {
      if (this.representanteId && payload.representante_id !== this.representanteId) {
        return;
      }
      this.notificaciones.agregarTurnoAsignado(payload);
      this.turnoAsignado$.next(payload);
    });
    this.socket.on('panel_jefatura_actualizar', (payload: PanelJefaturaActualizarEvento) => {
      this.panelJefaturaActualizar$.next(payload);
    });
    this.socket.on('socio_operacion', (payload: Record<string, unknown>) => {
      this.socioOperacion$.next(payload || {});
    });
    this.socket.on('connect_error', (err) => {
      console.warn('sdm-eventos error:', err.message);
    });
  }

  /** Cierra conexión de usuario interno (no toca lobby) */
  desconectar(): void {
    this.conectando = false;
    this.desconectarInterno();
    this.representanteId = null;
  }

  /** Cierra solo el socket del lobby público */
  desconectarLobby(): void {
    this.conectandoLobby = false;
    if (this.socketLobby) {
      this.socketLobby.removeAllListeners();
      this.socketLobby.disconnect();
      this.socketLobby = null;
    }
  }

  /** Libera el socket de sesión interna */
  private desconectarInterno(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  ngOnDestroy(): void {
    this.desconectar();
    this.desconectarLobby();
    this.turnoAsignado$.complete();
    this.panelJefaturaActualizar$.complete();
    this.lobbyAtencionActualizar$.complete();
    this.socioOperacion$.complete();
  }
}
